import Vec3 from "../../../shared/vec3";
import { ParticipantState, ColorConfig } from "../../../shared/interfaces";
import Scene = BABYLON.Scene;
import Mesh = BABYLON.Mesh;
import * as BABYLON from "babylonjs";
import ShadowGenerator = BABYLON.ShadowGenerator;
import ExpositionRendering from "../rendering/ExpositionRendering";

class Participant {
  id: string

  name: string
  position: Vec3
  direction: Vec3
  isWaling: Boolean

  hairColor: string
  skinColor: string
  shirtColor: string

  scene: Scene

  participantMesh: Mesh
  labelMesh: Mesh
  shadowGenerator: ShadowGenerator
  showLabel: boolean

  constructor(id: string, playerState: ParticipantState, scene: Scene, shadowGenerator: ShadowGenerator, showLabel: boolean = true) {
    this.id = id;
    this.name = playerState.name;
    this.position = playerState.position;
    this.direction = playerState.direction;
    this.isWaling = playerState.isWalking;

    this.hairColor = playerState.colors.hairColor;
    this.skinColor = playerState.colors.skinColor;
    this.shirtColor = playerState.colors.shirtColor;

    this.scene = scene;

    this.showLabel = showLabel

    this.shadowGenerator = shadowGenerator
    this.participantMesh = this.generateParticipantMesh()

 
    this.labelMesh = this.generateLabelMesh();
    this.labelMesh.isVisible = this.showLabel;
  }

  setLabelVisibleEnabled(showLabel: boolean){
    this.showLabel = showLabel;
    this.labelMesh.isVisible = this.showLabel;
  }

  updatePosition(newPosition: Vec3, direction: Vec3) {
    this.participantMesh.position.x = newPosition.x;
    this.participantMesh.position.z = newPosition.z;
    this.participantMesh.rotationQuaternion = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), Math.atan2(direction.x, direction.z));
    this.labelMesh.position.x = newPosition.x;
    this.labelMesh.position.z = newPosition.z;
  }

  generateParticipantMesh() {
    const transparentMaterial = new BABYLON.StandardMaterial("transparentMaterial", this.scene);
    transparentMaterial.alpha = 0;

    const hairMaterial = new BABYLON.StandardMaterial("skinMaterial" + this.hairColor, this.scene);
    hairMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.hairColor);

    const skinMaterial = new BABYLON.StandardMaterial("skinMaterial" + this.skinColor, this.scene);
    skinMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.skinColor);

    const shirtMaterial = new BABYLON.StandardMaterial("skinMaterial" + this.shirtColor, this.scene);
    shirtMaterial.diffuseColor = BABYLON.Color3.FromHexString(this.shirtColor);

    const playerMesh = BABYLON.Mesh.CreateBox(this.id, 1, this.scene, true)
    playerMesh.material = transparentMaterial;

    const body = BABYLON.Mesh.CreateBox('body', 1, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    body.material = shirtMaterial

    body.scaling.z = 0.25
    body.scaling.x = 0.5
    body.scaling.y = 0.7

    playerMesh.addChild(body)

    const head = BABYLON.Mesh.CreateBox('head', 0.5, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    head.material = skinMaterial
    head.scaling.y = 0.8;
    head.translate(BABYLON.Axis.Y, 0.7, BABYLON.Space.LOCAL);

    playerMesh.addChild(head)

    const hair = BABYLON.Mesh.CreateBox('hair', 0.5, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    hair.material = hairMaterial
    hair.scaling.y = 0.2;
    hair.translate(BABYLON.Axis.Y, 4.05, BABYLON.Space.LOCAL);

    playerMesh.addChild(hair)


    const arm1 = BABYLON.Mesh.CreateBox('arm1', 1, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    arm1.material = body.material
    arm1.translate(BABYLON.Axis.X, -0.3, BABYLON.Space.LOCAL);
    arm1.translate(BABYLON.Axis.Y, 0.1, BABYLON.Space.LOCAL);
    arm1.scaling.y = 0.4;
    arm1.scaling.x = 0.2;
    arm1.scaling.z = 0.2;

    playerMesh.addChild(arm1)

    const arm2 = BABYLON.Mesh.CreateBox('arm2', 1, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    arm2.material = body.material
    arm2.translate(BABYLON.Axis.X, 0.3, BABYLON.Space.LOCAL);
    arm2.translate(BABYLON.Axis.Y, 0.1, BABYLON.Space.LOCAL);
    arm2.scaling.y = 0.4;
    arm2.scaling.x = 0.2;
    arm2.scaling.z = 0.2;

    playerMesh.addChild(arm2)

    const hand1 = BABYLON.Mesh.CreateBox('hand1', 1, this.scene, true, BABYLON.Mesh.FRONTSIDE);
    hand1.material = skinMaterial
    hand1.translate(BABYLON.Axis.X, -0.3, BABYLON.Space.LOCAL);
    hand1.translate(BABYLON.Axis.Y, -0.15, BABYLON.Space.LOCAL);
    hand1.scaling.y = 0.1;
    hand1.scaling.x = 0.2;
    hand1.scaling.z = 0.2;
    playerMesh.addChild(hand1)

    const hand2 = BABYLON.Mesh.CreateBox('hand2', 1, this.scene, true, BABYLON.Mesh.FRONTSIDE);

    hand2.material = skinMaterial
    hand2.translate(BABYLON.Axis.X, 0.3, BABYLON.Space.LOCAL);
    hand2.translate(BABYLON.Axis.Y, -0.15, BABYLON.Space.LOCAL);
    hand2.scaling.y = 0.1;
    hand2.scaling.x = 0.2;
    hand2.scaling.z = 0.2;
    playerMesh.addChild(hand2)

    playerMesh.translate(BABYLON.Axis.Y, 0.5, BABYLON.Space.LOCAL);

    playerMesh.position.x = this.position.x;


    playerMesh.position.z = this.position.z;
    playerMesh.rotationQuaternion = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), Math.atan2(this.direction.x, this.direction.z));

    ExpositionRendering.addShadows(body, this.shadowGenerator);
    ExpositionRendering.addShadows(arm2, this.shadowGenerator);
    ExpositionRendering.addShadows(head, this.shadowGenerator);
    ExpositionRendering.addShadows(hair, this.shadowGenerator);
    ExpositionRendering.addShadows(arm1, this.shadowGenerator);
    ExpositionRendering.addShadows(hand1, this.shadowGenerator);
    ExpositionRendering.addShadows(hand2, this.shadowGenerator);

    return playerMesh;
  }

  generateLabelMesh() {


    const font = "bold 25px monospace"; //"28px Arial";
    const textColor = "black";

    const measurementTexture = new BABYLON.DynamicTexture("myPlane-measure-texture" + this.id, {}, this.scene, false);
    const dynamicTextureCtx = measurementTexture.getContext();
    dynamicTextureCtx.font = font;
    const fontWidth = dynamicTextureCtx.measureText(this.name).width;
    const fontHeight = dynamicTextureCtx.measureText(this.name).width;

    const basicResolution = 128;
    const width = Math.max(1, 1 * ((fontWidth + 20) / basicResolution));
    const fontOffset = 0.5 * (basicResolution * width - fontWidth);

    const height = 0.75;
    const textureWidth = width * basicResolution;
    const textureHeight = 0.5 * height * basicResolution;


    const textureOptions = {
      width: textureWidth,
      height: textureHeight
    }
    const dynamicTexture = new BABYLON.DynamicTexture("DynamicTexture" + this.id, textureOptions, this.scene, false);
    dynamicTexture.drawText(this.name, fontOffset, 30, font, "black", "white", true, true);

    const nameMaterial = new BABYLON.StandardMaterial("name" + this.id, this.scene);
    nameMaterial.diffuseTexture = dynamicTexture;
    nameMaterial.diffuseTexture.hasAlpha = true;
    nameMaterial.alpha = 0.75;

    const planeOptions = {
      width: 2.0 * width,
      height: 1.0 * height
    }
    const labelMesh = BABYLON.MeshBuilder.CreatePlane("myPlane" + this.id, planeOptions, this.scene);
    labelMesh.position.x = this.position.x;
    labelMesh.position.y = 2.5;
    labelMesh.position.z = this.position.z;
    labelMesh.rotationQuaternion = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), 0.25 * Math.PI);
    labelMesh.material = nameMaterial;
    return labelMesh;
  }

  remove() {
    this.participantMesh.dispose();
    this.labelMesh.dispose();
  }
}

export default Participant;